/** Carried in router `state` so a destination page knows where the user came from. */
export interface FromDescriptor {
  pathname: string
  search: string
  /** i18n key in the `common` namespace, e.g. 'nav.cameras'. */
  labelKey: string
}

/**
 * Build a query string that sets (or drops) ONE param and keeps every other.
 *
 * react-router does not merge a relative search string with the current one:
 * `resolvePath()` takes `search` verbatim from the `to` argument and only inherits
 * `pathname`. So `navigate('?event=42')` from `/events?category=human&camera=3`
 * lands on `/events?event=42` — every filter silently destroyed. Any same-page
 * navigate MUST hand the router a complete query string; build it with this.
 */
export function withSearchParam(
  search: string | URLSearchParams,
  key: string,
  value: string | null,
): string {
  const next = new URLSearchParams(search)
  if (value === null) next.delete(key)
  else next.set(key, value)
  const s = next.toString()
  return s ? `?${s}` : ''
}

/**
 * Fallback parents for deep links (shared URL, push notification), where history
 * has no in-app entry and navigate(-1) would eject the user out of the app.
 */
export const FALLBACK_PARENT: Record<string, FromDescriptor> = {
  '/events': { pathname: '/cameras', search: '', labelKey: 'nav.cameras' },
  '/cameras': { pathname: '/groups', search: '', labelKey: 'nav.groups' },
}

export interface LocationStateWithFrom {
  from?: FromDescriptor
  /**
   * Set on the history entry the event feed pushes when it opens the lightbox.
   *
   * Closing must pop that entry (`navigate(-1)`) rather than strip the param,
   * or the pushed entry is left dead in the stack and the next Back press
   * appears to do nothing. But a deep link (`/events?event=12` pasted into a
   * fresh tab) has no entry behind it — popping there ejects the user out of
   * the app entirely. `from` cannot stand in for this: a user who lands on
   * /events from the sidebar has no `from` yet still pushed a lightbox entry.
   */
  lightboxPushed?: boolean
}

/**
 * #297 — where a live alert opens its event, the same way a toast click does (PeekCards, #078):
 * `/events?event=N`. Already on /events, the user's filters are kept and the pushed entry is marked as a
 * lightbox entry (closing pops it); from another page it is a plain page transition.
 */
export function eventOpenTarget(
  location: { pathname: string; search: string; state: unknown },
  eventId: number,
): { to: { pathname: string; search: string }; options?: { state: LocationStateWithFrom } } {
  const onEvents = location.pathname === '/events'
  const to = { pathname: '/events', search: withSearchParam(onEvents ? location.search : '', 'event', String(eventId)) }
  if (!onEvents) return { to }
  return { to, options: { state: { ...(location.state as LocationStateWithFrom | null), lightboxPushed: true } } }
}
